import { FormProvider, useForm } from "react-hook-form";
import { TRADE_AMOUNT_TYPE, TRADE_TYPE } from "../constants";
import { useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import AmountType from "./AmountType";
import BasketSelect from "./BasketSelect";
import BrokerSelect from "./BrokerSelect";
import FormHeader from "./FormHeader";
import GoldTotal from "./GoldTotal";
import SideButtons from "./SideButtons";
import SubmitTrade from "./SubmitTrade";
import TradeInput from "./TradeInput";
import TradeRange from "./TradeRange";
import TradeTotal from "./TradeTotal";
import TradeType from "./TradeType";
import { calculateMaximumTradeAmount } from "utils/helpers/trade";
import { useSelector } from "react-redux";

function TradeForm({
  coinMarketDetails,
  setShowMarketDrawer,
}) {
  const { source, destination } = useParams();

  const tradeInsertForm = useSelector(
    (state) => state.trade?.tradeInsertForm?.data
  );

  const tradeAmountMax = useSelector(
    (state) => state.trade?.tradeAmountMax?.data
  );

  const [searchParams] = useSearchParams();
  const sideId = +searchParams.get("sideId") || 1;
  const amountTypeId = +searchParams.get("amountTypeId") || 1;
  const tradeTypeId = +searchParams.get("tradeTypeId") || 1;
  const basketId = +searchParams.get("basketId") || tradeInsertForm?.basketId;
  const brokerId = +searchParams.get("brokerId") || tradeInsertForm?.brokerId;

  const isPrice = useMemo(() => {
    const selectedTradeType = tradeInsertForm?.lisTradeType?.find(
      (tradeType) => tradeType?.id === tradeTypeId
    );

    return selectedTradeType?.isPrice;
  }, [tradeInsertForm?.lisTradeType, tradeTypeId]);

  const defaultValues = {
    basketId,
    brokerId,
    coinSourceId: +source,
    coinDestinationId: +destination,
    sideId,
    isPrice,
    amountTypeId,
    tradeTypeId,
    amount: null,
    price: null,
  };

  const methods = useForm({
    defaultValues,
    // resolver: yupResolver(tradeSchema()),
  });

  const { watch, reset } = methods;
  const coinSourceId = watch("coinSourceId");
  const coinDestinationId = watch("coinDestinationId");
  const price = watch("price");

  const sourceCoin = useMemo(() => {
    return tradeInsertForm?.lisCoinPair?.find((coin) => coin?.coinSourceId === +source);
  }, [source, tradeInsertForm?.lisCoinPair]);

  
  const destinationCoin = useMemo(() => {
    return tradeInsertForm?.lisCoinPair?.find((coin) => coin?.coinDestinationId === +destination);
  }, [destination, tradeInsertForm?.lisCoinPair]);

  const coinPairSetting = useMemo(
    () =>
      tradeInsertForm?.lisCoinPairFeeRateTrade?.find(
        (setting) =>
          setting?.coinSourceId === coinSourceId &&
          setting?.coinDestinationId === coinDestinationId &&
          setting?.brokerId === brokerId
      ),
    [tradeInsertForm?.lisCoinPairFeeRateTrade, coinSourceId, brokerId, coinDestinationId]
  );

  const srcDecimalCount = coinPairSetting?.decimalPlacesCoinSource;
  const destDecimalCount = coinPairSetting?.decimalPlacesCoinDestination;

  const amountPlaceholderCoin = useMemo(() => {
    const isAmount = amountTypeId === TRADE_AMOUNT_TYPE.amount.id;

    if (isAmount) {
      return coinMarketDetails?.coinSourceName;
    }

    return coinMarketDetails?.coinDestinationName;
  }, [amountTypeId, coinMarketDetails]);

  const minimumTradeAmount = useMemo(() => {
    const minSrcValue = coinPairSetting?.minimumTradeAmountCoinSource;
    const minDestValue = coinPairSetting?.minimumTradeAmountCoinDestination;

    if (amountTypeId === TRADE_AMOUNT_TYPE.amount.id) {
      return minSrcValue;
    }

    return minDestValue;
  }, [
    amountTypeId,
    coinPairSetting?.minimumTradeAmountCoinSource,
    coinPairSetting?.minimumTradeAmountCoinDestination,
  ]);

  const maximumTradeAmount = useMemo(
    () =>
      calculateMaximumTradeAmount({
        sideId,
        amountTypeId,
        tradeTypeId,
        tradeAmountMax,
        srcDecimalCount,
        destDecimalCount,
        price,
      }),
    [
      amountTypeId,
      srcDecimalCount,
      destDecimalCount,
      tradeAmountMax,
      price,
      sideId,
      tradeTypeId,
    ]
  );

  useEffect(() => {
    reset((formValues) => ({
      ...formValues,
      price: "",
      amount: "",
      coinSourceId: +source,
      coinDestinationId: +destination,
    }));
  }, [tradeTypeId, reset, amountTypeId, source, destination, sideId]);


  return (
    <FormProvider {...methods}>
      <form>
        <FormHeader
          setShowMarketDrawer={setShowMarketDrawer}
          coinMarketDetails={coinMarketDetails}
          sourceCoin={sourceCoin}
        />

        <BasketSelect />

        <BrokerSelect />

        <SideButtons />

        <TradeType />

        <AmountType />

        <TradeInput
          srcDecimalCount={srcDecimalCount}
          destDecimalCount={destDecimalCount}
          sourceCoin={sourceCoin}
          coinMarketDetails={coinMarketDetails}
          amountPlaceholderCoin={amountPlaceholderCoin}
          minimumTradeAmount={minimumTradeAmount}
          maximumTradeAmount={maximumTradeAmount}
          destinationCoin={destinationCoin}
        />

        <TradeRange
          srcDecimalCount={srcDecimalCount}
          destDecimalCount={destDecimalCount}
        />

        <GoldTotal sourceCoin={sourceCoin} />

        {tradeTypeId !== TRADE_TYPE.market.id && (
          <TradeTotal
            srcDecimalCount={srcDecimalCount}
            destDecimalCount={destDecimalCount}
          />
        )}

        <SubmitTrade
          amountPlaceholderCoin={amountPlaceholderCoin}
          minimumTradeAmount={minimumTradeAmount}
          maximumTradeAmount={maximumTradeAmount}
        />
      </form>
    </FormProvider>
  );
}

export default TradeForm;
